/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentProps } from 'preact';
import { TimeField } from './TimeField';
import type { Time, TimeGranularity, TimePlaceholders } from './types';
import { useRef } from 'preact/hooks';
import type { Dispatch } from 'preact/hooks';
import { timesAreDifferent } from '#utils/UNSAFE_timeUtils';
import { getTimeFieldInitialState } from './getTimeFieldInitialState';
import type { TimeFieldState } from './getTimeFieldInitialState';
import type { TimeReducerAction } from './timeReducer';
import type { BCP47Locale } from '#UNSAFE_IntlDateTime';
import { useTimeAriaLabels } from './useTimeAriaLabels';

type TimeFieldType = ComponentProps<typeof TimeField>;

type UseTimeResetProps = {
  dispatch: Dispatch<TimeReducerAction>;
  granularity: TimeGranularity;
  hasLeadingZeroForHour: boolean;
  locale: BCP47Locale;
  state: TimeFieldState;
  timeMasks: TimePlaceholders;
  value: TimeFieldType['value'];
};

/**
 * Hook that creates and updates a timeResetRef.
 * The timeResetRef is used to track whether the time value has been reset programmatically.
 *
 * When a programmatic update is detected, the hook dispatches a 'reset' action to update
 * the reducer state with a new initial state using the new currentValue.
 * After dispatching the 'reset' action,
 * timeResetRef.current is set to true to indicate that the time value has been reset
 * programmatically.
 *
 */
const useTimeReset = ({
  dispatch,
  granularity,
  hasLeadingZeroForHour,
  locale,
  state,
  timeMasks,
  value: currentValue
}: UseTimeResetProps) => {
  const ariaLabels = useTimeAriaLabels({ locale });

  // currentValue is the controlled value passed from the parent.
  const lastValueFromParentRef = useRef<Time | undefined>(currentValue);
  const timeResetRef = useRef<boolean>(false);

  // If a new value is being pushed from the parent, and that value differs from
  // the current value of the reducer, then this is a programmatic update
  // and we need to call 'reset' to update the reducer state. Otherwise, the only
  // time the reducer state is updated from the value is on mount.
  if (timesAreDifferent(lastValueFromParentRef.current, currentValue)) {
    lastValueFromParentRef.current = currentValue;
    if (timesAreDifferent(currentValue, state.currentTime)) {
      const initialState = getTimeFieldInitialState({
        ariaLabels,
        currentValue,
        hasLeadingZeroForHour,
        granularity,
        locale,
        timeMasks
      });
      dispatch({ actionType: 'reset', data: initialState });
      timeResetRef.current = true;
    }
  }

  return { timeResetRef };
};

export { useTimeReset };
